import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { platformAPI } from "../services/api";
import { groupByCategory, plural } from "../utils/grouping";
import TemplateProductForm from "../components/TemplateProductForm";
import ShopProductBrowser from "../components/ShopProductBrowser";
import RangeForm from "../components/RangeForm";
import Modal from "../components/Modal";
import SelectBox from "../components/SelectBox";
import { RangeCard, AddToRangeDialog } from "../components/RangesSection";

const API_ORIGIN = process.env.REACT_APP_API_URL || "http://localhost:3010/api";

const primaryBtn =
  "px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50";
const secondaryBtn = "px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm";

const errorText = (err, fallback) =>
  err.response?.data?.error || err.response?.data?.message || fallback;

function CopyToShopDialog({ templateId, itemIds, onClose, onCopied }) {
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    platformAPI.listShops().then(({ data }) => setShops(data));
  }, []);

  const copy = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await platformAPI.copyTemplate(templateId, shopId, itemIds);
      setDone({ count: data.length, shop: shops.find((s) => s.id === shopId)?.name });
      onCopied();
    } catch (err) {
      setError(errorText(err, "Failed to copy"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={done ? "Copied" : `Copy ${plural(itemIds.length, "product")} to a shop`} onClose={onClose}>
      {done ? (
        <>
          <p className="text-sm text-green-700">
            Copied {plural(done.count, "product")} to {done.shop}. They are waiting there as drafts.
          </p>
          <div className="flex justify-end mt-4">
            <button onClick={onClose} className={primaryBtn}>Done</button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-3">
            Prices are not copied: the shop sets its own, and the products arrive as drafts (hidden
            from its register and menu) until it does.
          </p>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            value={shopId}
            onChange={(e) => setShopId(e.target.value)}
            autoFocus
          >
            <option value="">— choose a shop —</option>
            {shops.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.is_active ? "" : " (suspended)"}
              </option>
            ))}
          </select>
          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={onClose} className={secondaryBtn}>Cancel</button>
            <button onClick={copy} disabled={busy || !shopId} className={primaryBtn}>
              {busy ? "Copying…" : `Copy ${plural(itemIds.length, "product")}`}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

function RemoveItemsDialog({ templateId, itemIds, onClose, onRemoved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await platformAPI.removeTemplateItems(templateId, itemIds);
      onRemoved();
    } catch (err) {
      setError(errorText(err, "Failed to remove"));
      setBusy(false);
    }
  };

  return (
    <Modal title={`Remove ${plural(itemIds.length, "product")}?`} onClose={onClose}>
      <p className="text-sm text-gray-700">
        They are removed from this template only. Products already copied to a shop, and the shops'
        own products they came from, are not touched.
      </p>
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      <div className="flex justify-end gap-3 mt-4">
        <button onClick={onClose} className={secondaryBtn}>Cancel</button>
        <button
          onClick={remove}
          disabled={busy}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50"
        >
          {busy ? "Removing…" : "Remove"}
        </button>
      </div>
    </Modal>
  );
}

function ItemRow({ item, groups, selecting, selected, onSelect, onEdit, onRemove, onAddToRange }) {
  return (
    <div className="px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
      {selecting && (
        <SelectBox
          checked={selected.has(item.id)}
          onChange={(on) => onSelect([item.id], on)}
          label={`Select ${item.name}`}
        />
      )}
      {(item.images?.[0] || item.image_url) ? (
        <img
          src={`${API_ORIGIN}${item.images?.[0] || item.image_url}`}
          alt=""
          className="w-10 h-10 rounded-lg object-cover border shrink-0"
        />
      ) : (
        <span className="w-10 h-10 shrink-0" />
      )}
      <div className="min-w-0 flex-1 basis-40">
        <p className="font-medium text-gray-800 truncate">{item.name}</p>
        <p className="text-xs text-gray-400 truncate">
          {[item.subcategory, item.brand, item.source_shop_name ? `from ${item.source_shop_name}` : "custom"]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <div className="space-x-3 shrink-0 text-sm">
        {groups.length > 0 && (
          <button onClick={() => onAddToRange(item)} className="text-primary-700 hover:underline">
            Add to range…
          </button>
        )}
        <button onClick={() => onEdit(item)} className="text-primary-700 hover:underline">
          Edit
        </button>
        <button onClick={() => onRemove(item)} className="text-red-600 hover:underline">
          Remove
        </button>
      </div>
    </div>
  );
}

export default function TemplateDetail() {
  const { id } = useParams();
  const [template, setTemplate] = useState(null);
  const [formTarget, setFormTarget] = useState(null); // "new" | item | null
  const [rangeTarget, setRangeTarget] = useState(null); // "new" | group | null
  const [addToRangeItem, setAddToRangeItem] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [error, setError] = useState("");

  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [collapsedCats, setCollapsedCats] = useState(() => new Set());
  const [expandedRanges, setExpandedRanges] = useState(() => new Set());
  const [dialog, setDialog] = useState(null); // "copy" | "remove"
  const [removeIds, setRemoveIds] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await platformAPI.getTemplate(id);
      setTemplate(data);
      setNameDraft(data.name);
      // Forget ticks on products that no longer exist
      const present = new Set(data.items.map((i) => i.id));
      setSelected((prev) => new Set([...prev].filter((itemId) => present.has(itemId))));
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load template");
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => template?.groups || [], [template]);
  const items = useMemo(() => template?.items || [], [template]);

  // One block per category, holding the ranges filed under it and the loose
  // products of it. `ids` is every product in the block, ranges' included.
  const sections = useMemo(() => {
    const byGroup = new Map(groups.map((g) => [g.id, items.filter((i) => i.variant_group_id === g.id)]));
    const entries = [
      ...groups.map((g) => ({ kind: "range", category: g.category, group: g })),
      ...items.filter((i) => !i.variant_group_id).map((i) => ({ kind: "item", category: i.category, item: i })),
    ];

    return groupByCategory(entries, (e) => e.category).map(({ category, entries: list }) => {
      const ranges = list.filter((e) => e.kind === "range").map((e) => e.group);
      const loose = list.filter((e) => e.kind === "item").map((e) => e.item);
      const ids = [...loose.map((i) => i.id), ...ranges.flatMap((g) => byGroup.get(g.id).map((i) => i.id))];
      return { category, ranges, loose, ids, byGroup };
    });
  }, [groups, items]);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!template) return <p className="text-gray-500">Loading…</p>;

  const saveName = async () => {
    if (nameDraft.trim() && nameDraft !== template.name) {
      await platformAPI.renameTemplate(id, nameDraft.trim());
    }
    setRenaming(false);
    await load();
  };

  const setMany = (ids, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((itemId) => (on ? next.add(itemId) : next.delete(itemId)));
      return next;
    });

  const toggleIn = (setter, key) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const expandAll = () => {
    setCollapsedCats(new Set());
    setExpandedRanges(new Set(groups.map((g) => g.id)));
  };
  const collapseAll = () => {
    setCollapsedCats(new Set(sections.map((s) => s.category)));
    setExpandedRanges(new Set());
  };

  const stopSelecting = () => {
    setSelecting(false);
    setSelected(new Set());
  };

  const allIds = items.map((i) => i.id);
  const allTicked = allIds.length > 0 && selected.size === allIds.length;
  const selectedIds = Array.from(selected);

  const askRemove = (ids) => {
    setRemoveIds(ids);
    setDialog("remove");
  };

  return (
    <div>
      <Link to="/product-templates" className="text-sm text-gray-500 hover:underline">
        ← Product templates
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-1 mb-4">
        <div className="flex items-center gap-3">
          {renaming ? (
            <>
              <input
                className="text-2xl font-bold text-gray-900 border-b border-gray-300 focus:outline-none"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                autoFocus
              />
              <button onClick={saveName} className="text-sm text-primary-700 hover:underline">Save</button>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-gray-900">{template.name}</h1>
              <button onClick={() => setRenaming(true)} className="text-sm text-gray-400 hover:underline">
                rename
              </button>
            </>
          )}
          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{template.status}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setRangeTarget("new")} className={secondaryBtn}>
            + New range
          </button>
          <button onClick={() => setFormTarget("new")} className={primaryBtn}>
            + New product
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3 text-sm">
        <span className="text-gray-500">
          {plural(items.length, "product")}
          {groups.length > 0 && ` · ${plural(groups.length, "range")}`}
        </span>
        <button
          onClick={() => (selecting ? stopSelecting() : setSelecting(true))}
          disabled={items.length === 0}
          className={`px-3 py-1.5 rounded-lg font-medium border disabled:opacity-50 ${
            selecting
              ? "bg-primary-600 border-primary-600 text-white hover:bg-primary-700"
              : "border-gray-300 hover:bg-gray-50"
          }`}
        >
          {selecting ? "Done selecting" : "Select"}
        </button>
        <span className="flex items-center gap-3 text-gray-500">
          <button onClick={expandAll} className="hover:underline">Expand all</button>
          <button onClick={collapseAll} className="hover:underline">Collapse all</button>
        </span>
      </div>

      {selecting && (
        <div className="sticky top-0 z-30 mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm shadow-sm">
          <label className="flex items-center gap-2 font-medium text-gray-800">
            <SelectBox
              checked={allTicked}
              indeterminate={selected.size > 0}
              onChange={(on) => setMany(allIds, on)}
              label="Select every product"
            />
            {selected.size} of {items.length} selected
          </label>
          {selected.size > 0 && (
            <button onClick={() => setSelected(new Set())} className="text-gray-500 hover:underline">
              Clear
            </button>
          )}
          <span className="flex-1" />
          <button
            onClick={() => askRemove(selectedIds)}
            disabled={selected.size === 0}
            className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 font-medium disabled:opacity-50"
          >
            Remove
          </button>
          <button
            onClick={() => setDialog("copy")}
            disabled={selected.size === 0}
            className={primaryBtn}
          >
            Copy to a shop…
          </button>
        </div>
      )}

      <div className="space-y-4">
        {sections.map(({ category, ranges, loose, ids, byGroup }) => {
          const collapsed = collapsedCats.has(category);
          const ticked = ids.filter((itemId) => selected.has(itemId)).length;

          return (
            <section key={category}>
              <div className="flex items-center gap-3 px-1 py-1.5">
                {selecting && (
                  <SelectBox
                    checked={ids.length > 0 && ticked === ids.length}
                    indeterminate={ticked > 0}
                    onChange={(on) => setMany(ids, on)}
                    label={`Select every ${category}`}
                  />
                )}
                <button
                  type="button"
                  onClick={() => toggleIn(setCollapsedCats, category)}
                  aria-expanded={!collapsed}
                  className="flex items-center gap-2 text-start"
                >
                  <span className={`text-gray-400 text-xs transition-transform ${collapsed ? "" : "rotate-90"}`}>▶</span>
                  <h2 className="text-base font-semibold text-gray-800 capitalize">{category}</h2>
                  <span className="text-xs text-gray-500">{ids.length}</span>
                </button>
              </div>

              {!collapsed && (
                <div className="space-y-2">
                  {ranges.map((group) => (
                    <RangeCard
                      key={group.id}
                      templateId={id}
                      group={group}
                      members={byGroup.get(group.id)}
                      onEditProduct={setFormTarget}
                      onChanged={load}
                      onEditRange={() => setRangeTarget(group)}
                      expanded={expandedRanges.has(group.id)}
                      onToggle={() => toggleIn(setExpandedRanges, group.id)}
                      selecting={selecting}
                      selected={selected}
                      onSelectItems={setMany}
                    />
                  ))}

                  {loose.length > 0 && (
                    <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
                      {loose.map((item) => (
                        <ItemRow
                          key={item.id}
                          item={item}
                          groups={groups}
                          selecting={selecting}
                          selected={selected}
                          onSelect={setMany}
                          onEdit={setFormTarget}
                          onRemove={(it) => askRemove([it.id])}
                          onAddToRange={setAddToRangeItem}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}

        {sections.length === 0 && (
          <p className="bg-white rounded-xl shadow px-4 py-8 text-center text-gray-400">
            No products yet — add some from an existing shop below, or create a brand-new one.
          </p>
        )}

        <ShopProductBrowser templateId={id} startOpen={items.length === 0} onAdded={load} />
      </div>

      {dialog === "copy" && (
        <CopyToShopDialog
          templateId={id}
          itemIds={selectedIds}
          onClose={() => {
            setDialog(null);
          }}
          onCopied={stopSelecting}
        />
      )}

      {dialog === "remove" && (
        <RemoveItemsDialog
          templateId={id}
          itemIds={removeIds}
          onClose={() => setDialog(null)}
          onRemoved={() => {
            setDialog(null);
            setSelected((prev) => new Set([...prev].filter((itemId) => !removeIds.includes(itemId))));
            load();
          }}
        />
      )}

      {addToRangeItem && (
        <AddToRangeDialog
          templateId={id}
          item={addToRangeItem}
          groups={groups}
          onClose={() => setAddToRangeItem(null)}
          onDone={() => {
            setAddToRangeItem(null);
            load();
          }}
        />
      )}

      {rangeTarget && (
        <RangeForm
          templateId={id}
          group={rangeTarget === "new" ? null : rangeTarget}
          onClose={() => setRangeTarget(null)}
          onSaved={() => {
            setRangeTarget(null);
            load();
          }}
        />
      )}

      {formTarget && (
        <TemplateProductForm
          templateId={id}
          item={formTarget === "new" ? null : formTarget}
          onClose={() => setFormTarget(null)}
          onSaved={() => {
            setFormTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
}
